import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot, doc, deleteDoc, updateDoc, orderBy, limit, getDocs, getDoc, addDoc } from 'firebase/firestore';
import { db, getCollectionPath } from '../../lib/firebase';
import { motion, AnimatePresence } from 'motion/react';
import {
  User,
  Trophy,
  History,
  TrendingUp,
  Shield,
  Hand,
  MapPin,
  Star,
  Settings,
  Edit,
  Camera,
  LogOut,
  ArrowRight,
  UserMinus,
  Minus,
  CalendarDays,
  AlertTriangle,
  Zap,
  Target,
  Award,
  Users,
  Clock,
  BadgeCheck,
  Swords,
  X,
  MessageSquare,
  Bell,
  Check,
  CalendarX,
  CalendarCheck,
  Mail
} from 'lucide-react';
import { UserProfile, Player, Match, Team, Tournament, Config, CategoryRequest, PublicMatch, PlayerStats } from '../../types';
import { formatPlayerName, formatPairName } from '../../lib/utils';
import { PairAvatar } from '../common/PairAvatar';
import { getCategoryStyles } from '../../lib/categoryStyles';
import { MatchCard } from '../match/MatchCard';
import { PlayerCardPremium } from './PlayerCardPremium';
import { requestNotificationPermission, disableNotifications } from '../../lib/notifications';
import { NotificationBlockedModal } from '../common/NotificationBlockedModal';
import { FadeInImage } from '../common/FadeInImage';

interface MyProfileProps {
  user: UserProfile;
  player: Player | null;
  players: Player[];
  config: Config;
  notifications?: any[];
  onMatchClick: (match: Match) => void;
  onPlayerClick: (playerId: string) => void;
  onEditProfile: () => void;
  onOpenSupportChat: (player: Player) => void;
  highlightValidation?: boolean;
  onTabChange?: (tab: string) => void;
}

export function MyProfile({
  user,
  player,
  players,
  config,
  notifications = [],
  onMatchClick,
  onPlayerClick,
  onEditProfile,
  onOpenSupportChat,
  highlightValidation = false,
  onTabChange
}: MyProfileProps) {
  const [showStreakInfo, setShowStreakInfo] = useState(false);
  const [showAllActivities, setShowAllActivities] = useState(false);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [cardScale, setCardScale] = useState(0.92);
  const [isEnablingNotifs, setIsEnablingNotifs] = useState(false);
  const [isBlockedModalOpen, setIsBlockedModalOpen] = useState(false);
  const [stats, setStats] = useState<PlayerStats | null>(null);
  const [showVerificationInfo, setShowVerificationInfo] = useState(false);
  useEffect(() => {
    const updateScale = () => {
      const w = window.innerWidth;
      const baseW = 1440;
      setCardScale(w < 1024 ? 1 : Math.min(1.2, w / baseW));
    };
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, []);

  useEffect(() => {
    if (!player?.id) return;
    const statsRef = doc(db, getCollectionPath('playerStats'), player.id);
    const unsub = onSnapshot(statsRef, snap => {
      if (snap.exists()) {
        setStats(snap.data() as PlayerStats);
      } else {
        setStats(null);
      }
    });
    return () => unsub();
  }, [player?.id]);

  useEffect(() => {
    if (!player?.id) return;
    const q = query(
      collection(db, 'categoryRequests'),
      where('playerId', '==', player.id)
    );
    const unsub = onSnapshot(q, (snapshot) => {
      const hasPending = snapshot.docs.some(doc => doc.data().status === 'pending');
      setHasPendingRequest(hasPending);
    });
    return () => unsub();
  }, [player?.id]);

  // --- Self-contained data fetching ---
  const [myMatches, setMyMatches] = useState<Match[]>([]);
  const [teamsMap, setTeamsMap] = useState<Map<string, Team>>(new Map());
  const [myTournaments, setMyTournaments] = useState<Tournament[]>([]);

  useEffect(() => {
    if (!player?.id) return;

    // Fetch the player's matches using participantIds index
    const matchQ = query(
      collection(db, getCollectionPath('matches')),
      where('participantIds', 'array-contains', player.id)
    );

    const unsub = onSnapshot(matchQ, async (snap) => {
      let docs = snap.docs.map(d => ({ id: d.id, ...(d.data() as Record<string, unknown>) } as unknown as Match));
      // Filter locally for completed matches and sort by createdAt
      docs = docs.filter(m => m.status === 'completed');
      docs.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      // Take top 100
      docs = docs.slice(0, 100);
      setMyMatches(docs);

      // Fetch only the team docs we actually need
      const teamIds = new Set<string>();
      docs.forEach(m => { teamIds.add(m.team1Id); teamIds.add(m.team2Id); });
      const missing = [...teamIds].filter(id => id && !teamsMap.has(id));

      if (missing.length > 0) {
        const batches: string[][] = [];
        for (let i = 0; i < missing.length; i += 30) batches.push(missing.slice(i, i + 30));
        const newEntries = new Map<string, Team>();
        for (const batch of batches) {
          const { documentId } = await import('firebase/firestore');
          const tSnap = await getDocs(query(
            collection(db, getCollectionPath('teams')),
            where(documentId(), 'in', batch)
          ));
          tSnap.docs.forEach(td => newEntries.set(td.id, { id: td.id, ...(td.data() as Record<string, unknown>) } as unknown as Team));
        }
        setTeamsMap(prev => new Map([...prev, ...newEntries]));
      }
    });
    return () => unsub();
  }, [player?.id]);

  useEffect(() => {
    if (!player?.id) return;
    // Fetch tournaments this player is registered in
    // Sort in-memory to avoid composite index requirement
    const tQ = query(
      collection(db, getCollectionPath('tournaments')),
      where('registeredPlayerIds', 'array-contains', player.id)
    );
    const unsub = onSnapshot(tQ, (snap) => {
      let docs = snap.docs.map(d => ({ id: d.id, ...(d.data() as Record<string, unknown>) } as unknown as Tournament));
      docs.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      docs = docs.slice(0, 30);
      setMyTournaments(docs);
    });
    return () => unsub();
  }, [player?.id]);

  const [sedes, setSedes] = useState<any[]>([]);
  useEffect(() => {
    const fetchSedes = async () => {
      const q = query(collection(db, getCollectionPath('users')), where('role', 'in', ['admin', 'superadmin']));
      const snap = await getDocs(q);
      setSedes(snap.docs.map(d => ({ uid: d.id, ...d.data() })));
    };
    fetchSedes();
  }, []);


  const isVerified = player?.isValidated && !hasPendingRequest;
  const isPending = !player?.isValidated || hasPendingRequest;

  if (!player) return (
    <div className="flex flex-col items-center justify-center p-20 text-center space-y-4">
      <div className="w-20 h-20 bg-theme-primary/10 rounded-full flex items-center justify-center theme-primary-text">
        <AlertTriangle size={40} />
      </div>
      <h3 className="text-xl font-black uppercase tracking-tighter">Perfil no encontrado</h3>
      <p className="text-white/40 max-w-xs mx-auto text-sm leading-relaxed">Hubo un error al sincronizar tus datos de jugador con la base de datos central.</p>
    </div>
  );

  const allPlayerMatches = useMemo(() => myMatches, [myMatches]);
  const teams: Team[] = useMemo(() => [...teamsMap.values()], [teamsMap]);
  const tournaments: Tournament[] = myTournaments;

  const completedMatches = allPlayerMatches.filter(m => m.status === 'completed');

  const wins = completedMatches.filter(m => {
    const myTeam = teams.find(t => (t.player1Id === player.id || t.player2Id === player.id || t.player3Id === player.id) && (t.id === m.team1Id || t.id === m.team2Id));
    return m.winnerId === myTeam?.id;
  }).length;
  const losses = completedMatches.length - wins;
  const winRate = completedMatches.length > 0 ? (wins / completedMatches.length) * 100 : 0;

  const activities = [
    ...allPlayerMatches.filter(m => m.status === 'completed').map(m => ({ type: 'match' as const, data: m, date: m.createdAt || Date.now() })),
    ...tournaments.flatMap(t => {
      const myTeamsInTournament = teams.filter(team => t.registeredTeamIds?.includes(team.id) || t.approvedTeamIds?.includes(team.id));
      const myTeam = myTeamsInTournament.find(team => team.player1Id === player.id || team.player2Id === player.id || team.player3Id === player.id);
      if (myTeam) {
        return [{ type: 'tournament_registration' as const, data: t, team: myTeam, date: myTeam.createdAt || Date.now() }];
      }
      return [];
    }),
    ...notifications.map(n => ({ type: 'notification' as const, data: n, date: n.createdAt || Date.now() }))
  ].sort((a, b) => b.date - a.date);

  const getActivityDetails = (a: any) => {
    if (a.type === 'match') {
      const myTeamId = teams.find(t => t.player1Id === player.id || t.player2Id === player.id || t.player3Id === player.id)?.id;
      const isWinner = a.data.winnerId === myTeamId;
      const isCompleted = a.data.status === 'completed';

      if (!isCompleted) {
        return {
          icon: <CalendarDays size={14} />,
          title: 'Próximo Match',
          subtitle: a.data.tournamentName || 'Match Programado',
          color: 'text-amber-400/50',
          points: null
        };
      }

      return {
        icon: <Zap size={14} />,
        title: isWinner ? 'Victoria' : 'Derrota',
        subtitle: a.data.tournamentName || 'Match Finalizado',
        color: isWinner ? 'theme-primary-text' : 'text-red-400',
        points: null
      };
    }
    if (a.type === 'tournament_registration') {
      return {
        icon: <Users size={14} />,
        title: 'Inscripción',
        subtitle: a.data.name,
        color: 'text-blue-400',
        points: null
      };
    }
    if (a.type === 'notification') {
      const n = a.data;
      let icon = <Bell size={14} />;
      let color = 'text-white/40';
      let title = n.title;
      const titleLower = n.title?.toLowerCase() || '';
      const bodyLower = n.body?.toLowerCase() || '';

      if (n.type === 'player_validation') {
        icon = <BadgeCheck size={14} />;
        color = 'text-emerald-400';
      } else if (n.points) {
        icon = <Trophy size={14} />;
        color = 'text-amber-400';
      } else if (n.type === 'tournament_registration') {
        icon = <Users size={14} />;
        color = 'text-blue-400';
      } else if (n.type === 'tournament_started') {
        icon = <Trophy size={14} />;
        color = 'theme-primary-text';
      } else if (titleLower.includes('cancelado') || bodyLower.includes('cancelado')) {
        icon = <CalendarX size={14} />;
        color = 'text-red-400';
      } else if (titleLower.includes('turno') || titleLower.includes('reserva') || bodyLower.includes('turno') || bodyLower.includes('reserva')) {
        icon = <CalendarCheck size={14} />;
        color = 'text-emerald-400';
      } else if (titleLower.includes('invitación') || bodyLower.includes('invitación')) {
        icon = <Mail size={14} />;
        color = 'text-blue-400';
      }

      return {
        icon,
        title,
        subtitle: n.body,
        color,
        points: n.points
      };
    }
    return {
      icon: <History size={14} />,
      title: 'Actividad',
      subtitle: '',
      color: 'text-white/20',
      points: null
    };
  };



  const handleActivityClick = (a: any) => {
    if (a.type === 'notification' && onTabChange) {
      const type = a.data?.type;
      if (['booking_accepted', 'booking_rejected', 'booking_paid', 'booking_cancelled'].includes(type)) {
        onTabChange('reservas');
      } else if (['tournament_started', 'tournament_registration'].includes(type)) {
        onTabChange('torneos');
      } else if (type === 'match_reported' || type === 'match_impugned') {
        onTabChange('partidos');
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('openPublicMatch', { detail: { matchId: a.data.matchId } }));
        }, 500);
      }
    } else if (a.type === 'tournament_registration' && onTabChange) {
      onTabChange('torneos');
    } else if (a.type === 'match') {
      onMatchClick(a.data);
    }
  };

  const streak = stats?.currentWinStreak || 0;
  const isEnRacha = streak >= 3;
  const multiplier = isEnRacha ? 1.5 : 1;

  const totalGamesWon = stats?.totalGamesWon || 0;
  const totalGamesLost = stats?.totalGamesLost || 0;
  const maxWinStreak = stats?.maxWinStreak || 0;

  const remontadasAttempted = stats?.remontadasAttempted || 0;
  const remontadasWon = stats?.remontadasWon || 0;
  const tieBreaksPlayed = stats?.tieBreaksPlayed || 0;
  const tieBreaksWon = stats?.tieBreaksWon || 0;
  const totalSetsPlayed = stats?.totalSetsPlayed || 0;
  const totalGamesInSets = stats?.totalGamesInSets || 0;
  const winRateHistory = stats?.winRateHistory || [];
  
  const chronMatches = [...completedMatches].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
  
  const remonRate = remontadasAttempted > 0 ? (remontadasWon / remontadasAttempted) * 100 : 0;
  const tbRate = tieBreaksPlayed > 0 ? (tieBreaksWon / tieBreaksPlayed) * 100 : 0;
  const avgGamesPerSet = totalSetsPlayed > 0 ? totalGamesInSets / totalSetsPlayed : 0;

  const sortedPartners = Object.entries(stats?.partnerHistory || ({} as Record<string, { name: string, photo?: string, played: number, won: number }>))
    .map(([id, s]: [string, any]) => ({ id, ...s }))
    .sort((a, b) => b.played - a.played);

  // Compute best rank across all circuits
  let bestRank = 0;
  let bestCircuitName = 'Global';
  let bestIsGlobal = true;
  let bestCircuitLogo = '';

  const sortedGlobal = [...players].filter(p => !p.isGuest).sort((a, b) => (b.rankingPoints?.global ?? (b.points || 0)) - (a.rankingPoints?.global ?? (a.points || 0)));
  const globalRank = sortedGlobal.findIndex(p => p.id === player.id) + 1;
  const globalPoints = player.rankingPoints?.global ?? (player.points || 0);

  if (globalPoints > 0) {
    bestRank = globalRank;
  }

  if (player.rankingPoints?.clubs) {
    Object.entries(player.rankingPoints.clubs).forEach(([clubId, points]) => {
      if (points > 0) {
        const sortedClub = [...players].filter(p => !p.isGuest).sort((a, b) => (b.rankingPoints?.clubs?.[clubId] || 0) - (a.rankingPoints?.clubs?.[clubId] || 0));
        const clubRank = sortedClub.findIndex(p => p.id === player.id) + 1;
        if (clubRank > 0 && (bestRank === 0 || clubRank < bestRank)) {
          bestRank = clubRank;
          const club = sedes.find(s => s.uid === clubId);
          bestCircuitName = club?.name || 'Sede Local';
          bestIsGlobal = false;
          bestCircuitLogo = club?.photoUrl || '';
        }
      }
    });
  }

  const isUnranked = bestRank === 0;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 20
      }
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="space-y-2 md:space-y-3 pb-20 relative"
    >

      {/* Título y Descripción Superior */}
      <motion.div
        variants={itemVariants}
        className="flex flex-col items-center gap-1 md:gap-1.5 px-4 md:px-0"
      >
        <div className="flex items-center justify-center gap-3 md:gap-4 relative -translate-x-3 md:-translate-x-6">
          <h2 className="text-4xl md:text-7xl font-black uppercase italic tracking-tighter text-white leading-none flex items-center gap-3 md:gap-4">
            <User className="text-white shrink-0 w-10 h-10 md:w-16 md:h-16 filter drop-shadow-[0_0_15px_rgba(255,255,255,0.5)] hover:scale-110 hover:rotate-3 transition-transform duration-300" />
            <span>MI PERFIL</span>
          </h2>
          <button
            onClick={onEditProfile}
            className="group absolute -right-12 md:-right-16 top-1/2 -translate-y-1/2 p-2 hover:bg-white/10 rounded-full transition-all"
            title="Editar Perfil"
          >
            <Edit size={22} className="text-white/60 group-hover:text-white transition-colors md:w-7 md:h-7" />
          </button>
        </div>
        
        {/* Perfil Verificado (Debajo del titulo) */}
        <motion.div
          layout
          animate={highlightValidation ? {
            scale: [1, 1.1, 1],
            transition: { repeat: 5, duration: 0.5 }
          } : {}}
        >
          {isVerified ? (
            <div onClick={() => setShowVerificationInfo(true)} className={`cursor-pointer flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-full shadow-[0_0_15px_rgba(16,185,129,0.15)] group/tag hover:bg-emerald-500/20 transition-all duration-300 backdrop-blur-md ${highlightValidation ? 'ring-2 ring-emerald-400 ring-offset-2 ring-offset-black' : ''}`}>
              <BadgeCheck size={11} className="text-emerald-400 group-hover/tag:scale-110 transition-transform" />
              <span className="text-[9px] font-black uppercase tracking-[0.12em] text-emerald-400">Verificado</span>
            </div>
          ) : (
            <div onClick={() => setShowVerificationInfo(true)} className={`cursor-pointer flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-full shadow-[0_0_15px_rgba(245,158,11,0.15)] group/tag hover:bg-amber-500/20 transition-all duration-300 backdrop-blur-md ${highlightValidation ? 'ring-2 ring-amber-400 ring-offset-2 ring-offset-black' : ''}`}>
              <Clock size={11} className="text-amber-400 animate-pulse group-hover/tag:scale-110 transition-transform" />
              <span className="text-[9px] font-black uppercase tracking-[0.12em] text-amber-400">En proceso</span>
            </div>
          )}
        </motion.div>
      </motion.div>

      <div className="relative rounded-[2.5rem] bg-white/[0.04] md:bg-white/[0.02] border border-white/10 backdrop-blur-md md:backdrop-blur-2xl shadow-2xl overflow-hidden group p-6 md:p-10 transform-gpu" style={{ willChange: 'transform' }}>
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
          backgroundSize: '24px 24px'
        }} />
        <div className="absolute top-0 right-0 w-96 h-96 bg-theme-primary/10 blur-[80px] -translate-y-1/2 translate-x-1/2 rounded-full pointer-events-none transform-gpu" />


        <div className="max-w-[2000px] mx-auto px-4 md:px-12 flex flex-col gap-10 xl:gap-16 relative z-10">
          {/* Fila Superior: Tarjeta + Estadísticas Principales */}
          <div className="flex flex-col xl:flex-row items-center xl:items-stretch gap-6 xl:gap-16">

            {/* Bloque Izquierdo: Tarjeta del Jugador (Siempre centrado en móvil/tablet) */}
            <motion.div
              layout
              variants={itemVariants}
              className="w-full xl:w-[340px] flex flex-col items-center gap-6"
            >


              <motion.div 
                layout 
                className="relative group/card transition-transform duration-500 hover:scale-[1.01] perspective-1000 w-full flex justify-center origin-top mb-2 transform-gpu"
                style={{ 
                  transform: `scale3d(${cardScale}, ${cardScale}, 1)`,
                  willChange: 'transform',
                  height: 340 * (4/3) * cardScale
                }}
              >
                <div className="w-[340px] shrink-0">
                  <PlayerCardPremium
                    player={player}
                    playerMatches={completedMatches}
                    matchesCount={stats?.totalCompletedMatches ?? completedMatches.length}
                    wins={stats?.cumulativeWins ?? wins}
                    losses={stats ? (stats.totalCompletedMatches - stats.cumulativeWins) : losses}
                    winRate={stats ? (stats.totalCompletedMatches > 0 ? (stats.cumulativeWins / stats.totalCompletedMatches) * 100 : 0) : winRate}
                    categoryRank={bestRank}
                    circuitName={bestIsGlobal ? '' : bestCircuitName}
                    circuitLogo={bestIsGlobal ? '' : bestCircuitLogo}
                    isUnranked={isUnranked}
                    config={config}
                    isEnRacha={isEnRacha}
                    onStreakClick={() => setShowStreakInfo(true)}
                  />
                </div>
              </motion.div>






            </motion.div>

            {/* Bloque Derecho: Resumen de Estadísticas */}
            <motion.div
              layout
              variants={itemVariants}
              className="flex-1 w-full flex flex-col gap-8"
            >
              {/* Actividad Reciente (Ahora al principio y ancho completo) */}
              <div className="group transition-all">
                <h4 className="text-[10px] font-black uppercase tracking-widest text-white/40 mb-4 flex items-center justify-center gap-2">
                  <History size={12} className="theme-primary-text" />
                  Actividad Reciente
                </h4>
                <div className="space-y-2">
                  {activities.slice(0, 3).map((a, i) => {
                    const details = getActivityDetails(a);
                    const isMatchReported = a.type === 'notification' && a.data?.type === 'match_reported' && a.data?.title === 'Resultado Cargado';
                    
                    return (
                      <div
                        key={i}
                        onClick={() => handleActivityClick(a)}
                        className="flex flex-col gap-2 p-2 px-3 rounded-xl bg-white/[0.03] border border-white/5 group hover:bg-white/[0.08] hover:border-theme-primary/20 active:scale-[0.99] transition-all cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg bg-zinc-900 flex items-center justify-center border border-white/5 shrink-0 ${details.color}`}>
                            {details.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-start gap-2">
                              <p className="text-[9px] font-black uppercase text-white leading-tight truncate">{details.title}</p>
                              {details.points && (
                                <span className="text-[8px] font-black theme-primary-text italic shrink-0">+{details.points} PTS</span>
                              )}
                            </div>
                            <p className="text-[8px] text-white/40 mt-0.5 font-medium leading-tight">{details.subtitle}</p>
                          </div>
                        </div>


                      </div>
                    );
                  })}
                  {activities.length > 3 && (
                    <button
                      onClick={() => setShowAllActivities(true)}
                      className="w-full mt-2 py-2 text-[9px] font-black uppercase tracking-[0.2em] text-white/20 hover:text-white/60 transition-colors flex items-center justify-center gap-2"
                    >
                      Ver más actividades
                    </button>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-1 items-center xl:items-start text-center xl:text-left">
                <h2 className="text-[9px] font-black uppercase text-white/30 tracking-[0.4em]">Analytics Hub</h2>
                <h1 className="text-2xl xl:text-4xl font-black italic text-white tracking-tighter leading-none uppercase">
                  Mis <span className="theme-primary-text">estadísticas</span>
                </h1>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 items-stretch">
                {/* 1. Forma Reciente - Ultra Compacto */}
                <motion.div layout className="bg-white/[0.03] rounded-2xl p-4 border border-white/10 flex flex-col items-center justify-center gap-2 hover:bg-white/[0.05] transition-all">
                  <div className="flex items-center gap-2 opacity-60 hover:opacity-100 transition-opacity" title="De izquierda (más antiguo) a derecha (más reciente)">
                    <span className="text-[7px] font-black uppercase text-white/50 tracking-[0.3em] italic">Forma</span>
                    <div className="flex items-center">
                      <div className="w-6 h-[1.5px] bg-white/40" />
                      <ArrowRight size={12} className="-ml-1.5 text-white/40" />
                    </div>
                  </div>
                  <div className="flex gap-1 justify-center">
                    {(() => {
                      const recent = stats?.recentForm?.slice(-8) || [];
                      const padded = Array.from({ length: 8 }).map((_, i) => {
                        const idx = i - (8 - recent.length);
                        return idx >= 0 ? recent[idx] : undefined;
                      });
                      
                      return padded.map((res, i) => {
                        if (!res) {
                          return (
                            <div
                              key={`empty-${i}`}
                              className="w-5 h-5 rounded border border-white/5 flex items-center justify-center text-[8px] font-black text-white/10 bg-white/[0.01]"
                            >
                              ?
                            </div>
                          );
                        }

                        const won = res === 'W';
                        const isLatest = i === 7;
                        
                        return (
                          <div
                            key={`forma-${i}`}
                            className={`w-5 h-5 rounded border flex items-center justify-center text-[8px] font-black relative ${
                              won ? 'border-emerald-500/20 text-emerald-400 bg-emerald-500/5' : 'border-red-500/20 text-red-400 bg-red-500/5'
                            } ${isLatest ? 'scale-105 z-10 shadow-[0_0_10px_rgba(255,255,255,0.15)] ring-1 ring-white/20 animate-[pulse_1s_infinite]' : ''}`}
                          >
                            {won ? 'W' : 'L'}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </motion.div>

                {/* 2. Rachas y Récords */}
                <div className="grid grid-cols-2 gap-2">
                  <motion.div layout className="bg-white/[0.03] rounded-2xl p-3 border border-white/10 flex flex-col items-center justify-center hover:bg-white/[0.05] transition-all text-center">
                    <p className="text-[7px] font-black uppercase text-white/30 tracking-widest mb-1">Racha Actual</p>
                    <p className={`text-xl font-black italic ${streak >= 3 ? 'text-orange-500' : 'text-white'}`}>{streak}W</p>
                  </motion.div>
                  <motion.div layout className="bg-white/[0.03] rounded-2xl p-3 border border-white/10 flex flex-col items-center justify-center hover:bg-white/[0.05] transition-all text-center">
                    <p className="text-[7px] font-black uppercase text-white/30 tracking-widest mb-1">Racha Máxima</p>
                    <p className="text-xl font-black italic theme-primary-text">{maxWinStreak}W</p>
                  </motion.div>
                </div>

                {/* 3. Efectividad en Games - Ultra Compacto Centrado */}
                <motion.div layout className="bg-white/[0.03] rounded-2xl p-3 border border-white/10 relative overflow-hidden group/games hover:bg-white/[0.05] transition-all text-center">
                  <div className="flex flex-col items-center gap-1 relative z-10">
                    <div className="flex flex-col items-center">
                      <span className="text-[7px] font-black uppercase text-theme-primary tracking-widest mb-0.5">Efectividad en games</span>
                      <span className="text-2xl font-black italic theme-primary-text leading-none tracking-tighter">
                        {((totalGamesWon / (totalGamesWon + totalGamesLost || 1)) * 100).toFixed(0)}%
                      </span>
                    </div>

                    <div className="w-full flex flex-col gap-1">
                      <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden border border-white/5">
                        <motion.div
                          initial={{ width: "0%" }}
                          animate={{ width: `${(totalGamesWon / (totalGamesWon + totalGamesLost || 1)) * 100}%` }}
                          transition={{ duration: 1.2, ease: "easeOut" }}
                          className="h-full bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.3)]"
                        />
                      </div>
                      <div className="flex justify-between text-[7px] font-black italic px-1 opacity-60">
                        <span className="text-emerald-400">{totalGamesWon}W</span>
                        <span className="text-red-400">{totalGamesLost}L</span>
                      </div>
                    </div>

                    <div className="flex flex-col items-center">
                      <span className="text-[7px] font-black uppercase text-white/30 tracking-widest mb-0.5">Total: {totalGamesWon + totalGamesLost}</span>
                    </div>
                  </div>
                </motion.div>

                {/* 4. Winrate Stats - Ultra Compacto Centrado */}
                <motion.div layout className="bg-white/[0.03] rounded-2xl p-3 border border-white/10 relative overflow-hidden group/winrate flex flex-col items-center justify-center hover:bg-white/[0.05] transition-all text-center">
                  <div className="relative z-10 w-full flex flex-col items-center">
                    <span className="text-[7px] font-black uppercase text-white/30 tracking-[0.2em] block mb-0.5">Winrate Global</span>
                    <div className="flex items-baseline justify-center gap-1 mb-1">
                      <span className="text-2xl font-black italic text-white leading-none tracking-tighter">{Math.round(winRate)}</span>
                      <span className="text-[10px] font-black theme-primary-text uppercase leading-none">%</span>
                    </div>
                    <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden border border-white/5">
                      <motion.div 
                        initial={{ width: "0%" }} 
                        animate={{ width: `${winRate}%` }} 
                        transition={{ duration: 1.2, ease: "easeOut" }} 
                        className="h-full bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.3)]" 
                      />
                    </div>
                  </div>
                </motion.div>


              </div>
            </motion.div>
          </div>

          {/* Fila Inferior: Gráfico y Estadísticas Avanzadas */}
          <motion.div
            layout
            variants={itemVariants}
            className="w-full px-4 md:px-0"
          >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch w-full">
              {/* Gráfico (Columna 7/12) */}
              <motion.div layout className="lg:col-span-7 bg-white/[0.02] rounded-[3rem] p-6 md:p-10 border border-white/5 flex flex-col relative overflow-hidden group/chart">
                <div className="absolute inset-0 bg-gradient-to-br from-theme-primary/5 to-transparent opacity-0 group-hover/chart:opacity-100 transition-opacity duration-700" />

                <div className="flex flex-col items-center justify-center gap-1 text-center mb-6 md:mb-10 relative z-10 w-full">
                  <span className="text-[10px] font-black uppercase text-white/40 tracking-[0.4em]">Trajectory Analysis</span>
                  <h3 className="text-xl font-black italic text-white uppercase tracking-tighter">Evolución de <span className="theme-primary-text">Winrate</span></h3>
                  <div className="flex items-center gap-2 px-2.5 py-1 bg-black/40 rounded-full border border-white/10 backdrop-blur-md mt-2">
                    <div className="w-1.5 h-1.5 bg-[#00ffcc] rounded-full animate-pulse shadow-[0_0_10px_rgba(0,255,204,0.6)]" />
                    <span className="text-[8px] font-black uppercase text-[#00ffcc] tracking-widest italic">live tracking</span>
                  </div>
                </div>

                <div className="flex-1 relative min-h-[160px] md:min-h-[250px] z-10">
                  <div className="absolute inset-0 flex flex-col justify-between z-0 opacity-50">
                    {[100, 75, 50, 25, 0].map(v => (
                      <div key={v} className="w-full flex items-center gap-6">
                        <span className="text-[8px] font-black text-white/20 w-8">{v}%</span>
                        <div className="flex-1 h-[1px] bg-white/[0.05]" />
                      </div>
                    ))}
                  </div>

                  <div className="absolute inset-0 left-12 right-4 bottom-0">
                    {winRateHistory.length > 0 ? (
                      <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none">
                        <defs>
                          <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.2" />
                            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                          </linearGradient>
                        </defs>
                        <motion.path
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: 1, opacity: 1 }}
                          transition={{ duration: 2, ease: "easeOut", delay: 1 }}
                          d={(() => {
                            const pts = winRateHistory.slice(-20).map((pt, i, arr) => ({ x: (i / (arr.length - 1 || 1)) * 100, y: 100 - pt.rate }));
                            if (pts.length < 2) return "";
                            let d = `M ${pts[0].x} ${pts[0].y}`;
                            for (let i = 0; i < pts.length - 1; i++) {
                              const curr = pts[i];
                              const next = pts[i + 1];
                              const cp1x = curr.x + (next.x - curr.x) / 3;
                              const cp2x = curr.x + (next.x - curr.x) * 2 / 3;
                              d += ` C ${cp1x} ${curr.y}, ${cp2x} ${next.y}, ${next.x} ${next.y}`;
                            }
                            return d;
                          })()}
                          fill="none"
                          stroke="#3b82f6"
                          strokeWidth="3"
                          strokeLinecap="round"
                          className="drop-shadow-[0_0_12px_rgba(59,130,246,0.5)]"
                        />
                        <motion.path
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 1.5, delay: 1.5 }}
                          d={(() => {
                            const pts = winRateHistory.slice(-20).map((pt, i, arr) => ({ x: (i / (arr.length - 1 || 1)) * 100, y: 100 - pt.rate }));
                            if (pts.length < 2) return "";
                            let d = `M ${pts[0].x} ${pts[0].y}`;
                            for (let i = 0; i < pts.length - 1; i++) {
                              const curr = pts[i];
                              const next = pts[i + 1];
                              const cp1x = curr.x + (next.x - curr.x) / 3;
                              const cp2x = curr.x + (next.x - curr.x) * 2 / 3;
                              d += ` C ${cp1x} ${curr.y}, ${cp2x} ${next.y}, ${next.x} ${next.y}`;
                            }
                            return d + ` L 100 100 L 0 100 Z`;
                          })()}
                          fill="url(#chartFill)"
                        />
                      </svg>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-[12px] font-black uppercase text-white/5 tracking-[0.5em] italic">Procesando datos históricos...</span>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>

              {/* Estadísticas Avanzadas (Columna 4/12) */}
              <motion.div layout className="lg:col-span-5 flex flex-col gap-6">
                <div className="bg-white/[0.02] rounded-[3rem] p-6 border border-white/5 flex-1 relative overflow-hidden group/advanced hover:bg-white/[0.04] transition-all">
                  <div className="absolute top-0 right-0 p-8 opacity-5 group-hover/advanced:opacity-10 transition-opacity">
                    <Swords size={80} className="theme-primary-text" />
                  </div>

                  <h4 className="text-[10px] font-black uppercase text-white/40 tracking-[0.4em] mb-6 text-center">Análisis Táctico Avanzado</h4>

                  <div className="space-y-6">
                    {/* Mentalidad Ganadora */}
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-xl bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 shadow-[0_0_15px_rgba(234,179,8,0.1)]">
                            <Zap size={15} />
                          </div>
                          <div className="flex flex-col">
                            <span className="text-[10px] font-black uppercase text-white/90 tracking-wider">Mentalidad Ganadora</span>
                            <span className="text-[8px] font-bold text-yellow-400/90 tracking-wide">Capacidad de remontar</span>
                          </div>
                        </div>
                        <span className="text-lg font-black italic text-yellow-400 leading-none">{remonRate.toFixed(0)}%</span>
                      </div>
                      <p className="text-[9px] text-white/40 leading-relaxed">
                        % de victorias tras perder el 1er set. Evalúa tu fortaleza mental para remontar.
                      </p>
                      <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden border border-white/5">
                        <motion.div
                          initial={{ width: "0%" }}
                          animate={{ width: `${remonRate}%` }}
                          transition={{ duration: 1.5, ease: "easeOut" }}
                          className="h-full bg-yellow-500 shadow-[0_0_10px_rgba(234,179,8,0.3)]"
                        />
                      </div>
                      <div className="text-center text-[8px] font-bold uppercase tracking-widest">
                        <span className="text-yellow-400/70">{remontadasWon} de {remontadasAttempted} ganados</span>
                      </div>
                    </div>

                    {/* Control bajo Presión */}
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-xl bg-[#bef264]/10 text-[#bef264] border border-[#bef264]/20 shadow-[0_0_15px_rgba(190,242,100,0.1)]">
                            <Target size={15} />
                          </div>
                          <div className="flex flex-col">
                            <span className="text-[10px] font-black uppercase text-white/90 tracking-wider">Control bajo Presión</span>
                            <span className="text-[8px] font-bold text-[#bef264]/90 tracking-wide">Frialdad en tie-breaks</span>
                          </div>
                        </div>
                        <span className="text-lg font-black italic text-[#bef264] leading-none">{tbRate.toFixed(0)}%</span>
                      </div>
                      <p className="text-[9px] text-white/40 leading-relaxed">
                        Efectividad en tie-breaks (6-6) y sets cerrados. Muestra tu frialdad bajo máxima tensión.
                      </p>
                      <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden border border-white/5">
                        <motion.div
                          initial={{ width: "0%" }}
                          animate={{ width: `${tbRate}%` }}
                          transition={{ duration: 1.5, ease: "easeOut" }}
                          className="h-full bg-[#bef264] shadow-[0_0_10px_rgba(190,242,100,0.3)]"
                        />
                      </div>
                      <div className="text-center text-[8px] font-bold uppercase tracking-widest">
                        <span className="text-[#bef264]/70">{tieBreaksWon} de {tieBreaksPlayed} ganados</span>
                      </div>
                    </div>

                    {/* Intensidad de Juego */}
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-xl bg-blue-500/10 text-blue-400 border border-blue-500/20 shadow-[0_0_15px_rgba(59,130,246,0.1)]">
                            <TrendingUp size={15} />
                          </div>
                          <div className="flex flex-col">
                            <span className="text-[10px] font-black uppercase text-white/90 tracking-wider">Intensidad de Juego</span>
                            <span className="text-[8px] font-bold text-blue-400/90 tracking-wide">Exigencia competitiva</span>
                          </div>
                        </div>
                        <span className="text-lg font-black italic text-blue-400 leading-none">{avgGamesPerSet.toFixed(1)}</span>
                      </div>
                      <p className="text-[9px] text-white/40 leading-relaxed">
                        Promedio de games por set. Un valor alto (+9.0) indica máxima paridad y exigencia física.
                      </p>
                      <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden border border-white/5">
                        <motion.div
                          initial={{ width: "0%" }}
                          animate={{ width: `${(avgGamesPerSet / 12) * 100}%` }}
                          transition={{ duration: 1.5, ease: "easeOut" }}
                          className="h-full bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.3)]"
                        />
                      </div>
                      <div className="text-center text-[8px] font-bold uppercase tracking-widest">
                        <span className="text-blue-400/70">{avgGamesPerSet.toFixed(1)} games por set</span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Historial de parejas */}
          <div className="bg-slate-950/30 md:bg-white/[0.02] border border-white/5 backdrop-blur-sm md:backdrop-blur-md rounded-[2.5rem] p-6 md:p-8 group hover:border-white/10 transition-all transform-gpu" style={{ willChange: 'transform' }}>
            <h4 className="text-[10px] font-black uppercase tracking-widest text-white/40 mb-6 flex items-center gap-2">
              <Users size={14} className="theme-accent-text" />
              Historial de parejas
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {sortedPartners.slice(0, 6).map(p => (
                <div key={p.id} onClick={() => onPlayerClick(p.id)} className="flex items-center justify-between p-3.5 rounded-2xl bg-white/5 border border-white/5 cursor-pointer hover:bg-white/10 transition-all group/p">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl overflow-hidden border border-white/10 shrink-0">
                      {p.photo ? <FadeInImage src={p.photo} className="w-full h-full object-cover" alt={p.name} /> : <User className="w-full h-full p-2 text-white/10" />}
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase text-white truncate max-w-[120px]">{p.name}</p>
                      <p className="text-[8px] text-white/30">{p.played} Partidos</p>
                    </div>
                  </div>
                  <ArrowRight size={12} className="text-white/20 group-hover/p:translate-x-0.5 transition-transform" />
                </div>
              ))}
              {sortedPartners.length === 0 && (
                <div className="col-span-full py-8 text-center border border-dashed border-white/5 rounded-2xl">
                  <span className="text-[8px] font-black text-white/10 uppercase italic">Sin parejas registradas</span>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-12 transform-gpu">
            <div className="lg:col-span-12 flex flex-col gap-6">
              <div id="profile-achievements" className="bg-slate-950/30 md:bg-white/[0.02] border border-white/5 backdrop-blur-sm md:backdrop-blur-md rounded-[2.5rem] p-6 flex-1 group hover:border-white/10 transition-all transform-gpu" style={{ willChange: 'transform' }}>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-amber-500/10 text-amber-400">
                      <Award size={16} />
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-widest text-white/40">Logros y Trofeos</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3.5">
                  {player.achievements?.filter(a => (a.type as string) !== 'super8').map((ach, i) => {
                    return (
                      <div 
                        key={i} 
                        className={`px-4 py-2.5 sm:px-5 sm:py-3.5 rounded-2xl text-center transition-all duration-500 flex flex-col items-center justify-center min-w-0 bg-white/5 border border-white/5 hover:bg-white/10`}
                      >
                        <div className="text-lg mb-1">🏅</div>
                        <p className="text-[7.5px] min-[360px]:text-[8.5px] md:text-[9px] font-black uppercase text-white/60 whitespace-normal break-words w-full block" title={ach.text}>
                          {ach.text}
                        </p>
                      </div>
                    );
                  })}
                  {(!player.achievements || player.achievements.length === 0) && (
                    <div className="col-span-2 py-4 text-center border border-dashed border-white/5 rounded-2xl">
                      <span className="text-[8px] font-black text-white/10 uppercase italic">Sin logros registrados</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Botón de chat de soporte al final */}
        <motion.div layout className="mx-auto flex justify-center mt-8 pb-4">
          <button
            onClick={() => onOpenSupportChat(player)}
            className="group/chat flex items-center gap-2 px-6 py-3 bg-white/[0.02] hover:bg-white/[0.06] border border-white/5 hover:border-white/10 text-white/40 hover:text-white transition-all duration-300 rounded-full font-black text-[10px] uppercase tracking-[0.2em] active:scale-95 backdrop-blur-sm shadow-lg"
          >
            <MessageSquare size={14} className="text-white group-hover/chat:scale-110 transition-transform" />
            <span>¿Necesitas ayuda? Contactar Soporte</span>
          </button>
        </motion.div>


      </div>

      {/* Modales */}
      <AnimatePresence>
        {showAllActivities && (
          <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowAllActivities(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-md bg-[#222530] border border-white/10 rounded-[2.5rem] p-8 shadow-2xl overflow-hidden"
            >
              <div className="absolute top-0 right-0 p-8">
                <button onClick={() => setShowAllActivities(false)} className="text-white/20 hover:text-white transition-colors">
                  <X size={20} />
                </button>
              </div>

              <div className="flex flex-col gap-1 mb-8">
                <span className="text-[10px] font-black uppercase text-theme-primary tracking-[0.4em]">Historial</span>
                <h3 className="text-2xl font-black italic text-white uppercase tracking-tighter">Actividad <span className="theme-primary-text">Reciente</span></h3>
              </div>

              <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-white/5">
                {activities.map((a, i) => {
                  const details = getActivityDetails(a);
                  const isMatchReported = a.type === 'notification' && a.data?.type === 'match_reported' && a.data?.title === 'Resultado Cargado';
                  return (
                    <div
                      key={i}
                      onClick={() => {
                        handleActivityClick(a);
                        setShowAllActivities(false);
                      }}
                      className="flex flex-col gap-2 p-4 rounded-2xl bg-white/[0.03] border border-white/5 group hover:bg-white/[0.08] hover:border-theme-primary/20 active:scale-[0.99] transition-all cursor-pointer"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl bg-zinc-900 flex items-center justify-center border border-white/5 ${details.color}`}>
                          {React.cloneElement(details.icon as React.ReactElement, { size: 20 })}
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <p className="text-[11px] font-black uppercase text-white leading-tight">{details.title}</p>
                            {details.points && (
                              <span className="text-[10px] font-black theme-primary-text italic">+{details.points} PTS</span>
                            )}
                          </div>
                          <p className="text-[9px] text-white/60 mt-1 line-clamp-2">{details.subtitle}</p>
                          <p className="text-[8px] text-white/20 mt-1 uppercase tracking-widest">{new Date(a.date).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                        </div>
                      </div>
                      

                    </div>
                  );
                })}
              </div>

              <button
                onClick={() => setShowAllActivities(false)}
                className="w-full mt-8 py-4 bg-white/[0.03] hover:bg-white/[0.08] border border-white/10 text-white rounded-2xl transition-all font-black uppercase text-[10px] tracking-[0.2em]"
              >
                Cerrar Historial
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal de Información de Racha */}
      <AnimatePresence>
        {showStreakInfo && (
          <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowStreakInfo(false)}
              className="absolute inset-0 bg-black/90 backdrop-blur-md"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-sm bg-zinc-950 border border-white/10 rounded-[2.5rem] p-8 shadow-2xl overflow-hidden"
            >
              <div className="absolute top-0 right-0 p-8">
                <button onClick={() => setShowStreakInfo(false)} className="text-white/20 hover:text-white transition-colors">
                  <X size={20} />
                </button>
              </div>

              <div className="flex flex-col items-center text-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-orange-500/20 flex items-center justify-center text-orange-500 shadow-[0_0_30px_rgba(249,115,22,0.2)]">
                  <Zap size={32} />
                </div>
                <div>
                  <h3 className="text-2xl font-black italic text-white uppercase tracking-tighter">Sistema de <span className="text-orange-500">Rachas</span></h3>
                  <p className="text-white/40 text-[10px] uppercase tracking-widest mt-2 font-bold leading-relaxed">
                    Mantén una racha de 3 o más victorias para activar el multiplicador x1.5 en tus puntos.
                  </p>
                </div>
              </div>

              <div className="mt-8 p-4 rounded-2xl bg-white/5 border border-white/10">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-[10px] font-black uppercase text-white/30">Tu Racha Actual</span>
                  <span className="text-xl font-black italic theme-primary-text">{streak} VICTORIAS</span>
                </div>
                <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min((streak / 3) * 100, 100)}%` }}
                    className={`h-full ${streak >= 3 ? 'bg-orange-500' : 'bg-theme-primary'}`}
                  />
                </div>
              </div>

              <button
                onClick={() => setShowStreakInfo(false)}
                className="w-full mt-8 py-4 bg-white text-black rounded-2xl transition-all font-black uppercase text-[10px] tracking-[0.2em] active:scale-95"
              >
                Entendido
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showVerificationInfo && (
          <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowVerificationInfo(false)}
              className="absolute inset-0 bg-black/20 backdrop-blur-md"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-sm bg-zinc-950 border border-white/10 rounded-3xl p-6 md:p-8 shadow-2xl overflow-hidden text-center flex flex-col items-center gap-4"
            >
              <div className="absolute top-0 right-0 p-6">
                <button onClick={() => setShowVerificationInfo(false)} className="text-white/20 hover:text-white transition-colors">
                  <X size={20} />
                </button>
              </div>
              <div className="w-16 h-16 rounded-full bg-theme-primary/10 flex items-center justify-center text-theme-primary mb-2 shadow-[0_0_30px_rgba(163,230,53,0.15)]">
                <BadgeCheck size={32} />
              </div>
              <h3 className="text-xl md:text-2xl font-black italic text-white uppercase tracking-tighter">
                Verificación de <span className="theme-primary-text">Perfil</span>
              </h3>
              <p className="text-white/60 text-xs md:text-sm leading-relaxed mt-2">
                Con la verificación podrás competir en torneos, reservar turnos y jugar partidos oficiales.
              </p>
              <button
                onClick={() => setShowVerificationInfo(false)}
                className="w-full mt-6 py-3 md:py-4 bg-white text-black font-black uppercase text-[10px] tracking-[0.2em] rounded-2xl active:scale-95 transition-transform hover:bg-theme-primary"
              >
                Entendido
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      <NotificationBlockedModal isOpen={isBlockedModalOpen} onClose={() => setIsBlockedModalOpen(false)} />
    </motion.div>
  );
}
